export const WORKSPACE_ID = "cvp";
export const MAX_NOTES_CHARS = 2000;
export const REVIEW_TYPE_LABEL = {
    document_review: "Document Review",
    redline_review: "Redline Review",
    standalone_review: "Standalone Review",
};
export const REVIEW_SECTION_LABEL = {
    internal_consistency: "Internal Consistency Review",
    defined_terms: "Defined Terms Review",
    loopholes: "Loophole / Ambiguity Review",
    adverse_scenarios: "Adverse Scenarios (Stress Test)",
    acceptable_changes: "Acceptable Changes",
};
/** Skippable sections per review type, in output order. */
export const SKIPPABLE_SECTIONS = {
    document_review: ["internal_consistency", "defined_terms", "loopholes", "adverse_scenarios"],
    standalone_review: ["internal_consistency", "defined_terms", "loopholes", "adverse_scenarios"],
    // The redline prompt's "New Loopholes / Ambiguities" section carries
    // its fallback check; "loopholes" maps to it.
    redline_review: ["acceptable_changes", "loopholes", "defined_terms"],
};
export const REVIEW_SCOPE_PRESETS = [
    {
        key: "full",
        label: "Full review",
        description: "Every section. Use for first drafts.",
        skip: [],
    },
    {
        key: "final_check",
        label: "Final check",
        description: "Comparison, adverse scenarios and summary — skips the drafting-level passes.",
        skip: ["internal_consistency", "defined_terms", "loopholes", "acceptable_changes"],
    },
    {
        key: "quick",
        label: "Quick pass",
        description: "Economic, security and default terms plus High-priority items only.",
        skip: [],
        quickPass: true,
    },
];
/**
 * The one model every review, repository extraction, search and chat uses.
 * There is deliberately no per-review or per-request choice. (The repository
 * search planner stays on Haiku — see PLAN_MODEL.)
 *
 * Opus 5.5 always thinks (adaptive; it cannot be disabled) and its thinking
 * tokens count against max_tokens, so every call's cap leaves room for it.
 * It also rejects forced tool_choice, temperature/top_p/top_k and assistant
 * prefill — structured outputs (output_config) replace forced tool calls.
 */
export const CLAUDE_MODEL = "claude-opus-5-5";
export const CHECKLIST_STATUS_LABEL = {
    will_address: "Will Address",
    not_an_issue: "Not An Issue",
    needs_discussion: "Need to Discuss as a Group",
};
export const MODEL_PRICING = {
    "claude-sonnet-4-6": {
        inputPerMTok: 3,
        outputPerMTok: 15,
        cacheWritePerMTok: 3.75,
        cacheWrite1hPerMTok: 6,
        cacheReadPerMTok: 0.3,
    },
    // Cache reads on Opus 5.5 are 0.05x input, not the usual 0.1x.
    "claude-opus-5-5": {
        inputPerMTok: 4,
        outputPerMTok: 20,
        cacheWritePerMTok: 5,
        cacheWrite1hPerMTok: 8,
        cacheReadPerMTok: 0.2,
    },
    "claude-opus-4-7": {
        inputPerMTok: 5,
        outputPerMTok: 25,
        cacheWritePerMTok: 6.25,
        cacheWrite1hPerMTok: 10,
        cacheReadPerMTok: 0.5,
    },
    "claude-haiku-4-5": {
        inputPerMTok: 1,
        outputPerMTok: 5,
        cacheWritePerMTok: 1.25,
        cacheWrite1hPerMTok: 2,
        cacheReadPerMTok: 0.1,
    },
};
export function computeCostUsd(model, usage) {
    const p = MODEL_PRICING[model];
    // cacheCreateTokens is the TOTAL written; the 1h subset bills at its own
    // (2x) rate and the remainder at the 5-minute (1.25x) rate.
    const create1h = Math.min(usage.cacheCreate1hTokens ?? 0, usage.cacheCreateTokens);
    const create5m = usage.cacheCreateTokens - create1h;
    return ((usage.inputTokens * p.inputPerMTok) / 1_000_000 +
        (usage.outputTokens * p.outputPerMTok) / 1_000_000 +
        (create5m * p.cacheWritePerMTok) / 1_000_000 +
        (create1h * p.cacheWrite1hPerMTok) / 1_000_000 +
        (usage.cacheReadTokens * p.cacheReadPerMTok) / 1_000_000);
}
export const MAX_UPLOAD_BYTES = 25 * 1024 * 1024;
export const SHARE_DURATION_OPTIONS = [
    { hours: 1, label: "1 hour" },
    { hours: 4, label: "4 hours" },
    { hours: 12, label: "12 hours" },
    { hours: 24, label: "24 hours" },
    { hours: 24 * 7, label: "1 week" },
];
export const MAX_SHARE_DURATION_HOURS = 24 * 7;
export const DEFAULT_SHARE_DURATION_HOURS = 24;
// ============================================================================
// Document Repository
// ----------------------------------------------------------------------------
// A separate corpus of historical/closed deal documents, queryable two ways:
//   1. Per-deal Q&A — chat scoped to one deal's full as-amended document set.
//   2. Repository-wide Q&A — cross-deal questions answered metadata-first
//      (filter the structured field index), then read matching deals' docs.
//
// Kept in its own Firestore tree (workspaces/{WORKSPACE_ID}/repositoryDeals,
// repositoryDocs, repositorySchema, repositoryAudit, repositoryConversations)
// so nothing couples to the live review tool's `deals/` collection.
//
// Conventions confirmed against real deal docs (see project memory):
//   - We are ALWAYS the lender/investor; the lending entity name varies
//     (Streeterville Capital, LLC is the most common of several). Never
//     assume the lender name — the Borrower (company) is the variable party.
//   - Money is multi-part: face (original principal) / OID / Transaction
//     Expense Amount / purchase price (cash funded).
//   - A/B deals carry multiple notes with different terms, so per-note facts
//     live in `tranches[]`, not flat deal-level scalars.
//   - Amendments are applied in date order to produce "as-amended" state and
//     are hard-linked to the document they amend via `parentDocId`.
// ============================================================================
/** Soft cap on documents per repository deal (ingestion guard). */
export const REPO_MAX_DOCS_PER_DEAL = 80;
/** Built-in doc-kind labels — also the fallback label source for rendering. */
export const REPO_DOC_KIND_LABEL = {
    note: "Note",
    purchase_agreement: "Purchase Agreement (SPA / NPA)",
    warrant: "Warrant",
    daca: "DACA",
    security_agreement: "Security Agreement",
    pledge_agreement: "Pledge Agreement",
    guaranty: "Guaranty",
    side_letter: "Side Letter",
    registration_rights: "Registration Rights Agreement",
    transfer_agent_letter: "Transfer Agent Letter",
    share_issuance_resolution: "Share Issuance Resolution",
    board_resolution: "Board Resolution",
    officer_certificate: "Officer's / Secretary's Certificate",
    intercreditor_agreement: "Intercreditor Agreement",
    subordination_agreement: "Subordination Agreement",
    escrow_agreement: "Escrow Agreement",
    irrevocable_instructions: "Irrevocable Transfer Agent Instructions",
    term_sheet: "Term Sheet",
    amendment: "Amendment",
    forbearance: "Forbearance Agreement",
    other: "Other",
};
/**
 * Seed catalog of document kinds, written to repositorySettings/docKinds on
 * first access. Team-editable in-app afterward. `isAmendment` marks kinds that
 * require a parent-document link.
 */
export const REPO_SEED_DOC_KINDS = [
    { key: "note", label: "Note", order: 10 },
    { key: "purchase_agreement", label: "Purchase Agreement (SPA / NPA)", order: 20 },
    { key: "warrant", label: "Warrant", order: 30 },
    { key: "daca", label: "DACA", order: 40 },
    { key: "security_agreement", label: "Security Agreement", order: 50 },
    { key: "pledge_agreement", label: "Pledge Agreement", order: 60 },
    { key: "guaranty", label: "Guaranty", order: 70 },
    { key: "side_letter", label: "Side Letter", order: 80 },
    { key: "registration_rights", label: "Registration Rights Agreement", order: 90 },
    { key: "transfer_agent_letter", label: "Transfer Agent Letter", order: 100 },
    { key: "share_issuance_resolution", label: "Share Issuance Resolution", order: 110 },
    { key: "board_resolution", label: "Board Resolution", order: 120 },
    { key: "officer_certificate", label: "Officer's / Secretary's Certificate", order: 130 },
    { key: "intercreditor_agreement", label: "Intercreditor Agreement", order: 140 },
    { key: "subordination_agreement", label: "Subordination Agreement", order: 150 },
    { key: "escrow_agreement", label: "Escrow Agreement", order: 160 },
    { key: "irrevocable_instructions", label: "Irrevocable Transfer Agent Instructions", order: 170 },
    { key: "term_sheet", label: "Term Sheet", order: 180 },
    { key: "amendment", label: "Amendment", isAmendment: true, order: 190 },
    { key: "forbearance", label: "Forbearance Agreement", isAmendment: true, order: 200 },
    { key: "other", label: "Other", order: 210 },
];
/**
 * Built-in amendment kinds (fallback). The live source of truth is the
 * editable doc-kind catalog's `isAmendment` flag.
 */
export const REPO_AMENDMENT_KINDS = ["amendment", "forbearance"];
/** Title-case a snake_case key as a label fallback. */
export function repoHumanizeKey(key) {
    return (REPO_DOC_KIND_LABEL[key] ??
        key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()));
}
export const REPO_SEED_ENTITIES = [{ name: "Streeterville Capital, LLC" }];
/**
 * Display label for a repository deal following the team convention:
 * deal #1 with a company is just the ticker; the Nth deal appends N.
 *   repoDealLabel("AIM", 4)  -> "AIM4"
 *   repoDealLabel("GOAI", 1) -> "GOAI"
 */
export function repoDealLabel(ticker, dealNumber) {
    const t = ticker.trim().toUpperCase();
    return dealNumber > 1 ? `${t}${dealNumber}` : t;
}
/**
 * The /repository/deals/:key segment for a deal: its readable label when that
 * label parses back to the same deal, otherwise its Firestore id. A ticker
 * that itself ends in a digit (AB2, deal 1) has a label that would resolve to
 * AB deal #2, so it must be linked by id. GET /api/repository/deals/:id
 * accepts either.
 */
export function repoDealRouteKey(deal) {
    const label = repoDealLabel(deal.ticker, deal.dealNumber);
    const parsed = parseRepoTicker(label);
    return parsed.ticker === deal.ticker.trim().toUpperCase() && parsed.dealNumber === deal.dealNumber
        ? label
        : deal.id;
}
/**
 * Parse a deal label ("AIM4", "GOAI", "SCNX2") into its base ticker and deal
 * number. Trailing digits are the deal number (default 1); the leading
 * letters are the ticker. The UI lets the user override the parse, since a
 * ticker that genuinely ends in a digit would parse ambiguously.
 */
export function parseRepoTicker(label) {
    const cleaned = label.trim().toUpperCase();
    const m = cleaned.match(/^(.*?)(\d+)$/);
    if (m && m[1]) {
        return { ticker: m[1], dealNumber: Number(m[2]) };
    }
    return { ticker: cleaned, dealNumber: 1 };
}
/**
 * Seed metadata catalog. Written to repositorySchema/{fields} on first run;
 * the team edits/extends it in-app afterward. Two layers: `core` (normalized,
 * filterable) and `prose` (the term-sheet-tool summary rows). Validated
 * against real Streeterville-style deal docs.
 */
/**
 * Canonical user-facing labels for the seeded `security_type` enum values.
 * The generic snake_case humanizer mangles these ("ab_notes" → "Ab Notes"),
 * so every surface that renders a security type must go through this map,
 * falling back to `repoHumanizeKey` for values added to the schema later.
 */
export const REPO_SECURITY_TYPE_LABEL = {
    convertible_note: "Convertible Note",
    non_convertible_note: "Non-Convertible Note",
    ab_notes: "A/B Notes",
    convertible_preferred: "Convertible Preferred",
    equity_line: "Equity Line",
    prepaid_advance_ppp: "Prepaid Advance / PPP",
    other: "Other",
};
export const REPO_SEED_SCHEMA_FIELDS = [
    // ---- Core, filterable -------------------------------------------------
    {
        key: "security_type",
        label: "Security Type",
        type: "enum",
        group: "core",
        filterable: true,
        order: 10,
        enumValues: [
            "convertible_note",
            "non_convertible_note",
            "ab_notes",
            "convertible_preferred",
            "equity_line",
            "prepaid_advance_ppp",
            "other",
        ],
        description: "Overall instrument type. Use 'ab_notes' for A/B Note structures, 'prepaid_advance_ppp' for prepaid purchase/PPP advances.",
    },
    {
        key: "ab_note_structure",
        label: "A/B Note Structure",
        type: "boolean",
        group: "core",
        filterable: true,
        order: 20,
        description: "True if the deal uses an A note + B note structure (e.g. A-1 Note funded in cash plus a B Note backed by a deposit account).",
    },
    {
        key: "has_daca",
        label: "Has DACA",
        type: "boolean",
        group: "core",
        filterable: true,
        order: 30,
        description: "True if a Deposit Account Control Agreement is part of the deal.",
    },
    {
        key: "total_face_value_usd",
        label: "Total Face Value",
        type: "usd",
        group: "core",
        filterable: true,
        order: 40,
        description: "Sum of the original principal (face value) across all notes/tranches.",
    },
    {
        key: "total_cash_funded_usd",
        label: "Total Cash Funded",
        type: "usd",
        group: "core",
        filterable: true,
        order: 50,
        description: "Sum of the purchase price actually funded across all notes (face less OID and any Transaction Expense Amount).",
    },
    {
        key: "interest_rate_pct",
        label: "Interest Rate (%)",
        type: "number",
        group: "core",
        filterable: true,
        order: 60,
        unit: "%",
        description: "Annual interest rate of the primary (A) note. Use the per-note rates in tranches for A/B detail.",
    },
    {
        key: "oid_pct",
        label: "OID (%)",
        type: "number",
        group: "core",
        filterable: true,
        order: 70,
        unit: "%",
        description: "Original issue discount as a percent of the PURCHASE PRICE (cash funded) — team convention: OID$ ÷ cash funded (e.g. $450K OID on $5M funded = 9%), not OID$ ÷ face value. Usually equals the note's stated rate.",
    },
    {
        key: "is_convertible",
        label: "Convertible",
        type: "boolean",
        group: "core",
        filterable: true,
        order: 80,
        description: "True if any note in the deal is convertible into equity.",
    },
    {
        key: "conversion_discount_pct",
        label: "Conversion Discount (%)",
        type: "number",
        group: "core",
        filterable: true,
        order: 90,
        unit: "%",
        description: "Discount to the reference price used in the conversion formula (e.g. 85% of VWAP → 15 here, or record the multiplier as stated — note which in the source quote).",
    },
    {
        key: "governing_law",
        label: "Governing Law",
        type: "enum",
        group: "core",
        filterable: true,
        order: 100,
        enumValues: ["Utah", "Illinois", "Nevada", "Delaware", "New York", "Other"],
        description: "Governing-law state of the notes / transaction documents.",
    },
    {
        key: "maturity_months",
        label: "Maturity (months)",
        type: "number",
        group: "core",
        filterable: true,
        order: 110,
        unit: "months",
        description: "Term to maturity in months for the primary note (as-amended — reflect maturity extensions from amendments).",
    },
    {
        key: "prepayment_premium_pct",
        label: "Prepayment Premium (%)",
        type: "number",
        group: "core",
        filterable: true,
        order: 120,
        unit: "%",
        description: "Prepayment amount as a percent of outstanding balance (e.g. 110, 115).",
    },
    {
        key: "deferments_allowed",
        label: "Deferments Allowed",
        type: "number",
        group: "core",
        filterable: true,
        order: 130,
        description: "Number of redemption/payment deferments the Company is permitted. May be set in the original deal terms OR granted later by amendment — read the full as-amended doc set.",
    },
    {
        key: "has_warrant",
        label: "Has Warrant",
        type: "boolean",
        group: "core",
        filterable: true,
        order: 140,
        description: "True if the deal includes a warrant.",
    },
    {
        key: "has_guaranty",
        label: "Has Guaranty",
        type: "boolean",
        group: "core",
        filterable: true,
        order: 150,
        description: "True if the deal includes a guaranty.",
    },
    {
        key: "registration_required",
        label: "Registration Required",
        type: "boolean",
        group: "core",
        filterable: true,
        order: 160,
        description: "True if the Company must register resale shares (e.g. Form S-3/S-1 shelf) within a deadline.",
    },
    {
        key: "listing_default_trigger",
        label: "Listing Default Trigger",
        type: "boolean",
        group: "core",
        filterable: true,
        order: 170,
        description: "True if loss of Nasdaq/NYSE listing is an event of default or trigger event.",
    },
    {
        key: "borrower_state",
        label: "Borrower State of Incorporation",
        type: "string",
        group: "core",
        filterable: true,
        order: 180,
        description: "State/jurisdiction of incorporation of the Borrower (company).",
    },
    {
        key: "closing_date",
        label: "Closing Date",
        type: "date",
        group: "core",
        filterable: true,
        order: 190,
        description: "Effective/closing date of the deal. Mirrored to the deal's closingDate for sorting.",
    },
    // ---- Prose summary rows (the term-sheet-tool rows) --------------------
    {
        key: "structure",
        label: "Structure",
        type: "string",
        group: "prose",
        order: 200,
        description: "Compressed summary of deal size + note structure (face, OID, cash funded, A/B split). Semicolons separate facts; ideally under ~30 words.",
    },
    {
        key: "interest",
        label: "Interest",
        type: "string",
        group: "prose",
        order: 210,
        description: "Interest terms across notes (rates, OID, accrual).",
    },
    {
        key: "conversion",
        label: "Conversion",
        type: "string",
        group: "prose",
        order: 220,
        description: "Conversion mechanics (formula, discount, VWAP lookback, floor); '—' if none.",
    },
    {
        key: "redemption",
        label: "Redemption",
        type: "string",
        group: "prose",
        order: 230,
        description: "Redemption rights (start month, monthly max, limited redemptions, failure penalties).",
    },
    {
        key: "prepayment",
        label: "Prepayment",
        type: "string",
        group: "prose",
        order: 240,
        description: "Prepayment terms (premium %, conditions, exclusions).",
    },
    {
        key: "selling_restriction",
        label: "Selling Restriction",
        type: "string",
        group: "prose",
        order: 250,
        description: "Volume/ownership selling limits (e.g. % of weekly volume, ownership cap); '—' if none.",
    },
    {
        key: "future_financing_restrictions",
        label: "Future Financing Restrictions",
        type: "string",
        group: "prose",
        order: 260,
        description: "Limits on the Company's future debt/equity (consent rights, ATM/variable-rate carve-outs).",
    },
    {
        key: "events_of_default",
        label: "Events of Default",
        type: "string",
        group: "prose",
        order: 270,
        description: "Key triggers/events of default and the default-interest/balance-increase consequences.",
    },
    {
        key: "fees",
        label: "Fees",
        type: "string",
        group: "prose",
        order: 280,
        description: "Transaction Expense Amount and other fees (legal/admin/diligence, extension fees).",
    },
    {
        key: "collateral_security",
        label: "Collateral / Security",
        type: "string",
        group: "prose",
        order: 290,
        description: "Collateral package (security agreement, pledge, guaranty, DACA/deposit account) and any collateral-release mechanics.",
    },
    {
        key: "amendments_summary",
        label: "Amendments Summary",
        type: "string",
        group: "prose",
        order: 300,
        description: "What each amendment changed, in date order, and the resulting as-amended position (e.g. maturity extensions, extension fees, deferrals granted); '—' if none.",
    },
];
